import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { FlagIcon } from '@heroicons/react/24/solid';

interface MilestoneNodeData {
  task: {
    id: string;
    name: string;
    description: string | null;
    status: 'pending' | 'in_progress' | 'completed' | 'blocked';
    due_date: string | null;
  };
  selected: boolean;
  onSelect: () => void;
  onStatusChange: (status: string) => void;
}

export const MilestoneNode = memo(({ data }: { data: MilestoneNodeData }) => {
  const { task, selected, onSelect } = data;

  const getStatusColor = () => {
    switch (task.status) {
      case 'completed':
        return 'bg-green-100 border-green-500';
      case 'in_progress':
        return 'bg-blue-100 border-blue-500';
      case 'blocked':
        return 'bg-red-100 border-red-500';
      default:
        return 'bg-gray-100 border-gray-400';
    }
  };

  const getTextColor = () => {
    switch (task.status) {
      case 'completed':
        return 'text-green-900';
      case 'in_progress':
        return 'text-blue-900';
      case 'blocked':
        return 'text-red-900';
      default:
        return 'text-gray-700';
    }
  };

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={`
          px-6 py-4 transform rotate-45 cursor-pointer transition-all duration-200
          ${getStatusColor()}
          ${selected ? 'ring-2 ring-offset-2 ring-blue-500' : ''}
          border-2
        `}
        style={{ width: '120px', height: '120px' }}
        onClick={onSelect}
      >
        <div 
          className="transform -rotate-45 flex flex-col items-center justify-center h-full"
          style={{ marginTop: '-10px' }}
        >
          <FlagIcon className={`h-6 w-6 mb-1 ${getTextColor()}`} />
          <h3 className={`text-xs font-bold text-center line-clamp-2 ${getTextColor()}`}>
            {task.name}
          </h3>
          {task.due_date && (
            <p className={`text-xs mt-1 ${getTextColor()} opacity-75`}>
              {new Date(task.due_date).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </>
  );
});